import { Toast } from '@components/Toast';
import { faAngleLeft, faCubes, faTrash } from '@fortawesome/pro-light-svg-icons';
import { faImagePolaroid } from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { UpdateProductDetails } from '@partful/entities/product/details';
import { useConfigurationContext } from '@partful/features/configurations/lib/configurationProvider';
import { useDeleteConfiguration } from '@partful/features/configurations/lib/useDeleteConfiguration';
import { Button } from '@partful/shared/ui/Button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogPanel } from '@partful/shared/ui/Dialog/Dialog';
import { Image } from '@partful/shared/ui/Image';
import {
  WizardPageContent,
  WizardPageFooter,
  WizardPageHeader,
  WizardPageLayout,
} from '@partful/shared/ui/layouts/WizardPageLayout';
import { Typography } from '@partful/shared/ui/Typography';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, Redirect } from 'react-router-dom';
import styles from './OverviewPage.module.scss';

export function OverviewPage() {
  const { t: i18n } = useTranslation();
  const configuration = useConfigurationContext();

  const form = useForm<UpdateProductDetails>({
    resolver: zodResolver(UpdateProductDetails),
    mode: 'onChange',
    defaultValues: { productName: configuration.name },
  });

  /* istanbul ignore next */
  const handleSubmit = async (_data: UpdateProductDetails) => {
    // todo add later
  };

  const {
    mutate,
    isLoading: isDeleting,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
  } = useDeleteConfiguration();

  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleConfirmDelete = () => {
    mutate({ accountId: configuration.account, configurationPID: configuration.PID });
  };

  if (isDeleteSuccess) {
    return <Redirect to="/instructions" />;
  }

  return (
    <>
      <WizardPageLayout>
        <WizardPageHeader>
          <div>
            <Button linkComponent={Link} to="/instructions">
              <FontAwesomeIcon icon={faAngleLeft} />
              {i18n('common:backButton')}
            </Button>
          </div>
          <Typography appearance="heading5" as="h1">
            <FontAwesomeIcon icon={faCubes} /> {configuration.name}
          </Typography>
          <Typography appearance="heading6" as="h2" data-testid="overview-title">
            {i18n('instructions:manage.overview.title')}
          </Typography>
        </WizardPageHeader>
        <WizardPageContent className={styles.contentDiv}>
          <div className={styles.thumbnailContainer} data-testid="thumbnail-container">
            <Image
              src={undefined}
              alt={configuration.name}
              className={styles.thumbnail}
              fallback={
                <div className={styles.noImage}>
                  <FontAwesomeIcon icon={faImagePolaroid} />
                  <Typography appearance="heading6">{i18n(`instructions:manage.overview.noImage`)}</Typography>
                </div>
              }
            />
          </div>
          <form
            className={styles.detailsForm}
            id="detailsForm"
            name="detailsForm"
            onSubmit={form.handleSubmit(/* istanbul ignore next */ (data) => handleSubmit(data))}
          >
            <div className={styles.formControl}>
              <Typography as="label" htmlFor="productName">
                {i18n('instructions:manage.overview.name.label')}
              </Typography>
              <Typography as="p">{configuration.name}</Typography>
            </div>
          </form>
          <div>
            <Button
              onClick={() => setConfirmDelete(true)}
              size="small"
              variant="destructive"
              className={styles.deleteButton}
              data-testid="delete-button"
            >
              <FontAwesomeIcon icon={faTrash} />
              {i18n(`instructions:common.deleteProduct`)}
            </Button>
          </div>
        </WizardPageContent>
        <WizardPageFooter />
      </WizardPageLayout>
      <Dialog
        open={confirmDelete}
        modal
        onClose={/* istanbul ignore next - not testing native <dialog> behaviour */ () => setConfirmDelete(false)}
      >
        <DialogPanel>
          <DialogHeader>
            <Typography as="p" appearance="heading5" overflow="hidden" textOverflow="ellipsis">
              {i18n('instructions:manage.overview.delete.confirm.title', { name: configuration.name })}
            </Typography>
          </DialogHeader>
          <DialogContent>{i18n('instructions:manage.overview.delete.confirm.message')}</DialogContent>
          <DialogFooter>
            <Button
              variant="tertiary"
              onClick={/* istanbul ignore next */ () => setConfirmDelete(false)}
              disabled={isDeleting}
            >
              {i18n('instructions:common.cancel')}
            </Button>
            <Button
              data-testid="confirm-delete-product"
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {i18n('instructions:common.delete')}
            </Button>
          </DialogFooter>
        </DialogPanel>
      </Dialog>
      <Toast
        dependency={isDeleteError}
        severity="error"
        title={i18n('iinstructions:manage.overview.delete.error.title', { name: configuration.name })}
        message={i18n('instructions:manage.overview.delete.error.message')}
        testId="delete-error-message"
      />
    </>
  );
}
